import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Tooltip,
    useTheme,
    Breadcrumbs,
    Link,
    Drawer,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination,
    Alert,
    AlertTitle,
} from '@mui/material';
import {
    Edit as EditIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    Visibility as VisibilityIcon,
    // PictureAsPdfIcon removed - PDF functionality disabled
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    WarningAmber as WarningAmberIcon,
    Schedule as ScheduleIcon,
    History as HistoryIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { getPoStatus } from '../../utils/poUtils';
import { getServiceOrder, deleteServiceOrder, getAvailableTransitions, checkTransitionImpact, transitionServiceOrder, checkCanComplete, serviceOrderService, getSubServiceOrders, createSubServiceOrder } from '../../services/serviceOrders';
import invoicingService from '../../services/invoicing';
import LoadingScreen from '../LoadingScreen';
import WorkflowStages from './WorkflowStages';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import ServiceOrderActivityLog from './ServiceOrderActivityLog';
import AssignmentManager from '../common/AssignmentManager';
import ServiceOrderCrewSection from './ServiceOrderCrewSection';
import DocumentDisplay from '../common/DocumentDisplay';
import DocumentUploadDialog from '../common/DocumentUploadDialog';
import InvoicePanel from '../invoicing/InvoicePanel';
import PaymentDialog from '../invoicing/PaymentDialog';
import JobSelectionDialog from '../invoicing/JobSelectionDialog';
import InvoiceViewPreferencesDialog from '../invoicing/InvoiceViewPreferencesDialog';
import QuotationUploadDialog from './QuotationUploadDialog';
import { useAuth } from '../../contexts/AuthContext';
import { getWorkflowStates } from '../../services/workflow';
import { toast } from 'react-toastify';
import { useFieldPermissions } from '../../utils/fieldPermissions';
import { formatDateTime, formatDateOnly, formatTimeOnly } from '../../utils/dateUtils';
import { useNotifications } from '../../contexts/NotificationContext';
import { getEquipment } from '../../services/equipment';
import { getConsumables } from '../../services/consumables';
import { normalizeList } from '../../services/api';
import { PrimaryButton, SecondaryButton, DestructiveButton, FilledDestructiveButton } from '../common/Buttons';
import { getServiceOrderStatusChipColor, getServiceOrderPriorityChipColor, formatStatusLabel } from '../../constants/statusColors';
import { toastApiError } from '../../utils/errorHandler';

// Helper function to check field permissions
const canEditField = (user, moduleName, fieldName) => {
    if (!user?.role?.permissions?.[moduleName]) return false;

    const modulePerms = user.role.permissions[moduleName];
    const fieldPerms = modulePerms.fields || {};

    if (!modulePerms.change) return false;

    if (fieldPerms[fieldName]) return fieldPerms[fieldName].edit === true;
    if (fieldPerms['*']) return fieldPerms['*'].edit === true;

    return modulePerms.change === true;
};

/**
 * `available_transitions` uses WorkflowTransitionSerializer: `to_state` is the state id only;
 * the human-readable name is exposed as `to_state_name`.
 */
const getTransitionToStateNameLc = (transition) => {
    if (!transition) return '';
    const ts = transition.to_state;
    if (ts != null && typeof ts === 'object' && ts.name != null && String(ts.name) !== '') {
        return String(ts.name).toLowerCase().trim();
    }
    if (transition.to_state_name != null && String(transition.to_state_name) !== '') {
        return String(transition.to_state_name).toLowerCase().trim();
    }
    return '';
};

/** Send Quotation, Revise Quotation, and any transition into the Quoted state. */
const transitionRequiresQuotationAttachment = (transition) => {
    const toName = getTransitionToStateNameLc(transition);
    const tName = (transition?.name ?? '').toLowerCase();
    return (
        toName === 'quoted'
        || (tName.includes('send') && tName.includes('quotation'))
        || (tName.includes('revise') && tName.includes('quotation'))
    );
};

const ServiceOrderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    // Redirect if ID is "create" - this should go to create page (but "new" is the correct route)
    useEffect(() => {
        if (id === 'create') {
            const searchParams = new URLSearchParams(window.location.search);
            const parentOrderId = searchParams.get('parent_order_id');
            if (parentOrderId) {
                navigate(`/service-orders/new?parent_order_id=${parentOrderId}`, { replace: true });
            } else {
                navigate('/service-orders/new', { replace: true });
            }
            return;
        }
    }, [id, navigate]);

    const [order, setOrder] = useState(null);
    const [isClosed, setIsClosed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [stages, setStages] = useState([]);
    const { user } = useAuth();
    const [workflowStates, setWorkflowStates] = useState([]);
    const [availableTransitions, setAvailableTransitions] = useState([]);
    const [transitionsBlockedReason, setTransitionsBlockedReason] = useState(null);
    const [transitionDialogOpen, setTransitionDialogOpen] = useState(false);
    const [selectedTransition, setSelectedTransition] = useState(null);
    const [transitionLoading, setTransitionLoading] = useState(false);
    const [transitionImpact, setTransitionImpact] = useState(null);
    const [showResourceWarning, setShowResourceWarning] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [quotationDialogOpen, setQuotationDialogOpen] = useState(false);
    const [pendingQuotationTransition, setPendingQuotationTransition] = useState(null);
    const [availableEquipment, setAvailableEquipment] = useState([]);
    const [availableConsumables, setAvailableConsumables] = useState([]);
    const [completionRequirements, setCompletionRequirements] = useState(null);
    const [canComplete, setCanComplete] = useState(true);
    const [subServiceOrders, setSubServiceOrders] = useState([]);
    const [loadingSubOrders, setLoadingSubOrders] = useState(false);
    const [createSubOrderDialogOpen, setCreateSubOrderDialogOpen] = useState(false);
    // Sub-service order filtering
    const [subOrderSearch, setSubOrderSearch] = useState('');
    const [subOrderStatusFilter, setSubOrderStatusFilter] = useState('');
    const [subOrderServiceTypeFilter, setSubOrderServiceTypeFilter] = useState('');
    const [subOrderPage, setSubOrderPage] = useState(0);
    const [subOrderRowsPerPage, setSubOrderRowsPerPage] = useState(10);
    const [showActivityLog, setShowActivityLog] = useState(false);
    const [emailReportTarget, setEmailReportTarget] = useState(null);
    const activityLogRef = useRef(null);

    // Invoicing states
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [paymentDialogInvoiceId, setPaymentDialogInvoiceId] = useState(null);
    const [pendingPaymentTransition, setPendingPaymentTransition] = useState(null);
    const [invoicePanelKey, setInvoicePanelKey] = useState(0);
    const [jobSelectionDialogOpen, setJobSelectionDialogOpen] = useState(false);
    const [viewPrefsDialogOpen, setViewPrefsDialogOpen] = useState(false);
    const staleRefreshTsRef = useRef(0);

    // Refresh activity log whenever the drawer is opened
    useEffect(() => {
        if (showActivityLog) {
            activityLogRef.current?.refresh();
        }
    }, [showActivityLog]);
    const { canEditField, getFieldPermissions, permissions } = useFieldPermissions('service_orders');
    const { refreshNotifications } = useNotifications();

    useEffect(() => {
        // Don't fetch if ID is "create" - redirect will handle it
        if (id === 'create') {
            return;
        }
        // Scroll to top when viewing a service order, unless there's a hash to scroll to
        if (!location.hash) {
            window.scrollTo(0, 0);
        }
        fetchOrder();
        fetchWorkflowStates();
        fetchTransitions();
        // Only fetch available inventory if the current user has inventory view permission.
        // Technicians and other roles without inventory access get a 403 which we suppress here.
        if (user?.role?.permissions?.inventory?.view !== false) {
            fetchAvailableInventory();
        }
        fetchCompletionRequirements();
        fetchSubServiceOrders();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Scroll to hash element after order loads (e.g., #sub-service-orders)
    useEffect(() => {
        if (location.hash && order && !loading) {
            const elementId = location.hash.slice(1); // Remove # prefix
            const element = document.getElementById(elementId);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        }
    }, [location.hash, order, loading]);

    const fetchOrder = async () => {
        try {
            const data = await getServiceOrder(id);
            setOrder(data);
            setStages(data.workflow_states || []);

            // Check if service order is closed
            if (data.status && (data.status.name === 'Closed' || data.status.name === 'Cancelled')) {
                setIsClosed(true);
            } else {
                setIsClosed(false);
            }

            setError(null);

            // If the activity log drawer is open, refresh it to show any new entries
            // that resulted from the action that triggered fetchOrder()
            activityLogRef.current?.refresh();

            // Return the fetched data for use in performTransition
            return data;
        } catch (error) {
            console.error('Error fetching service order:', error);
            if (error.response?.status === 404) {
                setError('Service order not found.');
            } else if (error.response?.status === 403) {
                setError("You don't have permission to view this service order.");
            } else {
                setError('Failed to load service order. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchWorkflowStates = async () => {
        try {
            const response = await getWorkflowStates();
            setWorkflowStates(response.data);
        } catch (error) {
            console.error('Error fetching workflow states:', error);
            toastApiError(toast.error, error, 'Failed to load workflow states', 'view workflow states');
        }
    };

    const fetchTransitions = async () => {
        if (!id) return;
        try {
            const response = await getAvailableTransitions(id);
            // Response may be { transitions, blocked_reason } or a plain array (older API)
            if (Array.isArray(response.data)) {
                setAvailableTransitions(response.data);
                setTransitionsBlockedReason(null);
            } else {
                setAvailableTransitions(response.data.transitions || []);
                setTransitionsBlockedReason(response.data.blocked_reason || null);
            }
        } catch (error) {
            console.error('Error fetching transitions:', error);
            setAvailableTransitions([]);
            setTransitionsBlockedReason(null);
            // 502 is a transient gateway/infra error — suppress the toast so users
            // aren't alarmed on every page load when the upstream is momentarily down.
            const status = error?.response?.status;
            if (status !== 502 && status !== 503) {
                toastApiError(toast.error, error, 'Failed to load workflow actions', 'perform workflow actions');
            }
        }
    };

    // When workflow changes elsewhere (e.g. customer accepts/rejects from the quotation email), refetch so
    // Accept/Reject/Revise quotation buttons refresh without requiring a hard reload.
    useEffect(() => {
        if (id === 'create' || !id) {
            return undefined;
        }

        const refreshFromExternalChange = () => {
            const now = Date.now();
            if (now - staleRefreshTsRef.current < 1500) return;
            staleRefreshTsRef.current = now;
            void Promise.all([fetchOrder(), fetchTransitions()]).catch(() => { });
        };

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                refreshFromExternalChange();
            }
        };

        window.addEventListener('focus', refreshFromExternalChange);
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            window.removeEventListener('focus', refreshFromExternalChange);
            document.removeEventListener('visibilitychange', onVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchAvailableInventory = async () => {
        try {
            const [equipmentRes, consumablesRes] = await Promise.all([
                getEquipment({ status: 'available', serviceOrderId: id }),
                getConsumables({ status: 'available', serviceOrderId: id, page_size: 50 })
            ]);
            setAvailableEquipment(normalizeList(equipmentRes));
            setAvailableConsumables(consumablesRes);
        } catch (error) {
            console.error('Error fetching available inventory:', error);
            // 403 means this user's role doesn't have inventory view permission — suppress
            // the toast since it would be confusing/noisy (e.g. for Technician role).
            // 502/503 are transient infra errors — also suppress to avoid noise.
            const status = error?.response?.status;
            if (status !== 403 && status !== 502 && status !== 503) {
                toastApiError(toast.error, error, 'Failed to load available inventory', 'view inventory');
            }
        }
    };

    const fetchCompletionRequirements = async () => {
        try {
            const response = await checkCanComplete(id);
            setCompletionRequirements(response.data.requirements);
            setCanComplete(response.data.can_complete);
        } catch (error) {
            console.error('Error fetching completion requirements:', error);
            toast.error('Could not load completion requirements; you may still complete the order.');
            // Default to allowing completion if we can't check
            setCanComplete(true);
        }
    };

    const fetchSubServiceOrders = async () => {
        if (!id) return;
        try {
            setLoadingSubOrders(true);
            const data = await getSubServiceOrders(id);
            setSubServiceOrders(data || []);
        } catch (error) {
            console.error('Error fetching sub-service orders:', error);
            setSubServiceOrders([]);
            toastApiError(toast.error, error, 'Failed to load sub-service orders', 'view sub-service orders');
        } finally {
            setLoadingSubOrders(false);
        }
    };

    const handleCreateSubServiceOrder = () => {
        // Navigate to create page with parent order ID (use /new instead of /create to avoid route conflict)
        navigate(`/service-orders/new?parent_order_id=${id}`);
    };

    const handleDelete = async () => {
        try {
            await deleteServiceOrder(id);
            navigate('/service-orders');
        } catch (error) {
            console.error('Error deleting service order:', error);
            toast.error(error.response?.data?.message || error.response?.data?.detail || 'Failed to delete service order');
        }
    };

    const handleStageClick = (stage) => {
    };

    const handleMoveToNextStage = async (transition) => {
        setSelectedTransition(transition);
        setTransitionLoading(true);

        const toStateId = transition?.to_state?.id || transition?.to_state;
        if (!transition || !toStateId) {
            setSelectedTransition(null);
            setTransitionLoading(false);
            toast.error('No valid transition selected.');
            return;
        }

        try {
            // Intercept "Generate Invoice" transition - create invoice but DON'T transition SO yet
            // SO transition to "Invoiced" will happen when the invoice is sent
            const isGenerateInvoice = transition?.name?.toLowerCase().includes('generate invoice');
            if (isGenerateInvoice) {
                try {
                    // If parent SO has sub-service orders, show job selection dialog
                    if (subServiceOrders.length > 0) {
                        setJobSelectionDialogOpen(true);
                        setTransitionLoading(false);
                        return;
                    }

                    // Guard: redirect to existing invoice if one already exists
                    if (order.invoice_id) {
                        toast.info('An invoice already exists for this service order.');
                        navigate(`/invoicing/${order.invoice_id}/edit`);
                        setTransitionLoading(false);
                        return;
                    }

                    // No sub-orders — show view preferences dialog before creating invoice
                    setViewPrefsDialogOpen(true);
                    setTransitionLoading(false);
                    return;
                } catch (error) {
                    const errorMsg = error.response?.data?.error || error.message || 'Failed to create invoice';
                    toast.error(errorMsg);
                    setTransitionLoading(false);
                    return;
                }
            }

            // Intercept "Record Payment" transition to show PaymentDialog instead of immediate SO state change
            const isRecordPayment = transition?.name?.toLowerCase().includes('record payment');
            if (isRecordPayment && order?.invoice_id) {
                setPendingPaymentTransition({ transition, toStateId });
                setPaymentDialogInvoiceId(order.invoice_id);
                setPaymentDialogOpen(true);
                setTransitionLoading(false);
                return;
            }

            // Check if transitioning to 'completed' state
            const isCompleting = getTransitionToStateNameLc(transition) === 'completed' ||
                transition?.name?.toLowerCase().includes('complete');

            if (isCompleting && !canComplete) {
                // Show completion requirements error
                const missingReports = completionRequirements?.missing_reports || [];
                const flashMissing = missingReports.some(report => report.includes('Flash Report'));
                const detailedMissing = missingReports.some(report => report.includes('Detailed Report'));
                const subOrdersMissing = missingReports.some(report => report.includes('Incomplete sub-orders'));
                const incompleteSubOrders = completionRequirements?.incomplete_sub_orders || [];

                const reasons = [];
                if (flashMissing && detailedMissing) {
                    reasons.push('create and complete both a Flash Report and a Detailed Report');
                } else if (flashMissing) {
                    reasons.push('create and complete a Flash Report');
                } else if (detailedMissing) {
                    reasons.push('create and complete a Detailed Report');
                }
                if (subOrdersMissing && incompleteSubOrders.length > 0) {
                    reasons.push(`complete all sub-service orders (${incompleteSubOrders.length} incomplete: ${incompleteSubOrders.slice(0, 5).join(', ')}${incompleteSubOrders.length > 5 ? '...' : ''})`);
                }

                const errorMessage = reasons.length > 0
                    ? `Cannot complete service order. Please ${reasons.join(' and ')} before completing this service order.`
                    : 'Cannot complete service order. Please complete the required reports before completing this service order.';

                toast.error(errorMessage);
                setTransitionLoading(false);
                return;
            }

            // Send / revise quotation → same upload-or-pick-document flow before applying transition
            if (transitionRequiresQuotationAttachment(transition)) {
                // Store the pending transition and show quotation dialog
                setPendingQuotationTransition({ transition, toStateId });
                setQuotationDialogOpen(true);
                setTransitionLoading(false);
                return;
            }

            // First check the impact of the transition
            const impactResponse = await checkTransitionImpact(id, toStateId);
            const impact = impactResponse.data;

            // Check if transitioning to 'closed' state - don't show resource warning for closed
            const isClosing = getTransitionToStateNameLc(transition) === 'closed' ||
                transition?.name?.toLowerCase().includes('close');

            // Only show resource warning dialog for transitions that require confirmation, have resources,
            // and are NOT closing (i.e., only for completing)
            if (!isClosing && impact.requires_confirmation && impact.impact?.has_resources) {
                // Show resource warning dialog
                setTransitionImpact(impact);
                setShowResourceWarning(true);
                setTransitionLoading(false);
                return;
            }

            // Proceed with transition if no resources will be released or if it's closing
            await performTransition(transition, toStateId);
        } catch (error) {
            console.error('Transition error:', error);
            const errorMessage = error.response?.data?.detail || 'Failed to perform transition. Please try again.';
            toast.error(errorMessage);
            setTransitionLoading(false);
        }
    };

    const performTransition = async (transition, toStateId, notes = '') => {
        try {
            const response = await transitionServiceOrder(id, toStateId, notes);
            toast.success(`Successfully ${transition.name.toLowerCase()}`);

            setSelectedTransition(null);
            setTransitionImpact(null);
            setShowResourceWarning(false);

            // Refresh data
            const [updatedOrder] = await Promise.all([
                fetchOrder(),
                fetchTransitions()
            ]);

            // NOTE: "Generate Invoice" transition no longer auto-creates invoices.
            // Invoices are created explicitly via the invoicing API.
            // SO status transition to "Invoiced" now happens when invoice is sent.

            // Refresh notification count immediately after workflow transition
            // This ensures the notification bell updates when notifications are created
            await refreshNotifications();
            // fetchOrder() above already refreshes the activity log
        } catch (error) {
            console.error('Transition error:', error);
            const errorMessage = error.response?.data?.detail || 'Failed to perform transition. Please try again.';
            toast.error(errorMessage);
        } finally {
            setTransitionLoading(false);
        }
    };

    // Handler for quotation dialog — optional email + document (see QuotationUploadDialog)
    const handleQuotationProceed = async (documentId, { sendQuotationEmail = false } = {}) => {
        if (!pendingQuotationTransition) return;

        const { transition, toStateId } = pendingQuotationTransition;
        setTransitionLoading(true);

        try {
            const response = await transitionServiceOrder(
                id,
                toStateId,
                '',
                documentId ?? null,
                sendQuotationEmail,
            );
            toast.success(`Successfully ${transition.name.toLowerCase()}`);

            setSelectedTransition(null);
            setPendingQuotationTransition(null);

            // Refresh data
            await Promise.all([
                fetchOrder(),
                fetchTransitions()
            ]);

            await refreshNotifications();
        } catch (error) {
            console.error('Transition error:', error);
            const errData = error.response?.data;
            let errorMessage = 'Failed to perform transition. Please try again.';
            if (typeof errData?.detail === 'string') {
                errorMessage = errData.detail;
            }
            if (errData?.code === 'QUOTATION_PRIMARY_CONTACT_REQUIRED') {
                await fetchOrder();
            }
            toast.error(errorMessage);
        } finally {
            setTransitionLoading(false);
        }
    };

    // Check if user can upload documents based on permissions and service order status
    const canUploadInvoice = () => {
        if (!order || !user) {
            return false;
        }

        if (isClosed) {
            return false;
        }

        const canUpload = user.role?.permissions?.documents?.add;
        const canEditServiceOrder = user.role?.permissions?.service_orders?.change;

        // User needs both document upload permissions AND ability to edit service orders
        return canUpload === true && canEditServiceOrder === true;
    };

    // Check if user owns the current workflow state based on permissions, not hardcoded role names
    const isOwner = order && user && order.current_state && order.current_state.owner_role && user.role &&
        (user.role.permissions?.service_orders?.change === true);
    const isSuperuser = user && user.is_superuser;
    const hasAnyEditPermission = React.useMemo(() => {
        const fieldPerms = getFieldPermissions();
        return Object.values(fieldPerms).some(perm => perm && perm.edit);
    }, [getFieldPermissions]);
    const canEditOrDelete = isOwner || isSuperuser || hasAnyEditPermission;
    const currentOwnerRole = order?.current_state?.owner_role?.name;
    const canDelete = isSuperuser || (permissions?.service_orders?.delete === true);

    // Check if user is a customer (has service_orders permissions but not workflow permissions)
    const isCustomer = React.useMemo(() => {
        if (!user || !user.role) return false;
        // Check if user's role name is 'Customer' (case-insensitive)
        const roleName = user.role.name?.toLowerCase() || '';
        return roleName === 'customer';
    }, [user]);

    // Check if user can perform a specific transition
    const canPerformTransition = (transition) => {
        if (isSuperuser) return true;
        if (!user || !user.role) return false;

        // For customers: only allow Accept Quotation and Reject Quotation actions
        // Block Cancel Service Order
        if (isCustomer) {
            const transitionName = transition?.name?.toLowerCase() || '';
            const isCancelAction = transitionName.includes('cancel');
            const isAcceptQuotation = transitionName.includes('accept') && transitionName.includes('quotation');
            const isRejectQuotation = transitionName.includes('reject') && transitionName.includes('quotation');

            // Block cancel actions, only allow accept/reject quotation
            if (isCancelAction) {
                return false;
            }
            // Only allow accept/reject quotation for customers
            return isAcceptQuotation || isRejectQuotation;
        }

        // Check if the transition has conditions and if the user's role is allowed
        // The backend sets conditions like: {is_superadmin: true, is_technician: true}
        // We need to check if the user's role matches any of the true conditions

        if (!transition.conditions) return false;

        // Build the role key (e.g. 'Technician' becomes 'is_technician')
        const roleSlug = user.role.name.toLowerCase().replace(/\s+/g, '_');
        const roleKey = `is_${roleSlug}`;

        let isRoleAllowed = transition.conditions[roleKey] === true;

        // Admin role workflows often mark accountant/superadmin slots; backend may omit is_admin until migrated.
        if (!isRoleAllowed && roleSlug === 'admin') {
            isRoleAllowed = transition.conditions.is_accountant === true
                || transition.conditions.is_superadmin === true
                || transition.conditions.is_admin === true;
        }

        return isRoleAllowed;
    };

    // Document upload handlers
    const handleUploadClick = () => {
        setUploadDialogOpen(true);
    };

    const handleUploadSuccess = () => {
        setUploadDialogOpen(false);
        fetchOrder(); // Refresh to show new documents — also refreshes activity log
    };

    const handleCloseUploadDialog = () => {
        setUploadDialogOpen(false);
    };

    if (loading) {
        return <LoadingScreen message="Loading service order…" />;
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error" variant="h6" gutterBottom>
                    {error}
                </Typography>
                <PrimaryButton
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/service-orders')}
                    sx={{ mt: 2 }}
                >
                    Back to Service Orders
                </PrimaryButton>
            </Box>
        );
    }

    if (!order) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">Service order not found</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 4, mb: 4, p: 3 }}>
            <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
                <Link
                    component="button"
                    variant="body2"
                    color="text.secondary"
                    onClick={() => navigate('/service-orders')}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                    Service Orders
                </Link>
                <Typography variant="body2" color="text.primary">
                    {order.display_name || order.name || `SO-${order.id}`}
                </Typography>
            </Breadcrumbs>

            {/* PO Expired / Expiring Soon High-Visibility Alert Banner */}
            {(() => {
                const poStatus = getPoStatus(order?.po_expiry_date, order?.po_number);
                if (poStatus.isExpired) {
                    return (
                        <Alert
                            severity="error"
                            icon={<WarningAmberIcon fontSize="inherit" sx={{ color: '#ef4444' }} />}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.08)',
                                alignItems: 'center',
                                '& .MuiAlert-message': { width: '100%' }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#991b1b' }}>
                                        Purchase Order Expired / Exhausted
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#7f1d1d', mt: 0.5 }}>
                                        Purchase Order <strong>{order.po_number || '(No PO Number)'}</strong> expired on <strong>{formatDateOnly(order.po_expiry_date)}</strong> ({poStatus.description}). Further work and invoicing may be held until a renewed PO is obtained from the customer.
                                    </Typography>
                                </Box>
                                {canEditOrDelete && (
                                    <Chip
                                        label="Update PO Details"
                                        size="small"
                                        onClick={() => navigate(`/service-orders/${id}/edit`)}
                                        sx={{
                                            backgroundColor: '#fee2e2',
                                            color: '#991b1b',
                                            border: '1px solid #fca5a5',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: '#fecaca',
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        </Alert>
                    );
                }
                if (poStatus.isExpiringSoon) {
                    return (
                        <Alert
                            severity="warning"
                            icon={<ScheduleIcon fontSize="inherit" />}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                boxShadow: '0 2px 10px rgba(237, 108, 2, 0.15)',
                                alignItems: 'center',
                                '& .MuiAlert-message': { width: '100%' }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                                        Purchase Order Expiring Soon
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
                                        Purchase Order <strong>{order.po_number || '(No PO Number)'}</strong> expires on <strong>{formatDateOnly(order.po_expiry_date)}</strong> ({poStatus.description}). Please initiate PO renewal with the customer.
                                    </Typography>
                                </Box>
                                {canEditOrDelete && (
                                    <Chip
                                        label="Update PO Details"
                                        size="small"
                                        onClick={() => navigate(`/service-orders/${id}/edit`)}
                                        sx={{ backgroundColor: '#fff3e0', color: 'warning.dark', fontWeight: 'bold', cursor: 'pointer' }}
                                    />
                                )}
                            </Box>
                        </Alert>
                    );
                }
                return null;
            })()}

            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <SecondaryButton
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/service-orders')}
                    >
                        Back to list
                    </SecondaryButton>
                    <Box>
                        <Tooltip title="Refresh data">
                            <span>
                                <IconButton
                                    color="info"
                                    onClick={() => {
                                        setLoading(true);
                                        fetchOrder();
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={canEditOrDelete ? '' : 'You do not have permission to edit this service order.'}>
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={() => navigate(`/service-orders/${id}/edit`)}
                                    disabled={!canEditOrDelete}
                                >
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {canDelete && (
                            <Tooltip title={canDelete ? '' : 'You do not have permission to delete this service order.'}>
                                <span>
                                    <IconButton
                                        color="error"
                                        onClick={() => setDeleteDialogOpen(true)}
                                        disabled={!canDelete}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Service Order Title with page-level status/priority */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Service Order {(() => {
                            if (order.display_name) return order.display_name;
                            if (order.name && order.name.trim() !== '') return order.name;
                            return `SO-${order.id}`;
                        })()}
                    </Typography>
                    {order.status && (
                        <Chip
                            label={formatStatusLabel(order.status.name)}
                            color={getServiceOrderStatusChipColor(order.status.name)}
                            size="small"
                            sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                        />
                    )}
                    {order.priority && (
                        <Chip
                            label={formatStatusLabel(order.priority.name)}
                            color={getServiceOrderPriorityChipColor(order.priority.name)}
                            size="small"
                            sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                        />
                    )}
                    {order.parent_order && (
                        <Chip
                            label="Sub-Service Order"
                            color="info"
                            sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                        />
                    )}
                    {order.sub_service_orders_count > 0 && (
                        <Chip
                            label={`Parent (${order.sub_service_orders_count} sub-order${order.sub_service_orders_count > 1 ? 's' : ''})`}
                            color="secondary"
                            sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                        />
                    )}
                    {order.service_type && (
                        <Typography variant="body1" color="text.secondary">
                            - {order.service_type.name}
                        </Typography>
                    )}
                    {/* Activity Log Toggle Button — pushed to the right */}
                    <Tooltip title={showActivityLog ? 'Hide Activity Log' : 'Show Activity Log'}>
                        <span style={{ marginLeft: 'auto' }}>
                            {showActivityLog ? (
                                <PrimaryButton
                                    startIcon={<HistoryIcon fontSize="small" />}
                                    onClick={() => setShowActivityLog((prev) => !prev)}
                                >
                                    Activity
                                </PrimaryButton>
                            ) : (
                                <SecondaryButton
                                    startIcon={<HistoryIcon fontSize="small" />}
                                    onClick={() => setShowActivityLog((prev) => !prev)}
                                >
                                    Activity
                                </SecondaryButton>
                            )}
                        </span>
                    </Tooltip>
                </Box>
                {order.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {order.description}
                    </Typography>
                )}
            </Box>

            <WorkflowStages
                stages={order.workflow_states || []}
                history={order.workflow_history || []}
                currentStage={order.current_state}
                onStageClick={handleStageClick}
                ownerRole={order.current_state?.owner_role?.name}
                serviceOrder={order}
            />

            {/* Available Transitions Section */}
            {availableTransitions.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        color: 'primary.main',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        pb: 1
                    }}>
                        Available Actions
                    </Typography>
                    <Paper sx={{ p: 3, borderRadius: 4 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {availableTransitions
                                .filter(transition => canPerformTransition(transition))
                                .filter(transition => {
                                    // Sub-SOs skip the quotation phase entirely
                                    if (order?.parent_order && transition?.name?.toLowerCase().includes('quotation')) return false;

                                    // Hide "Record Payment" when invoice is VOID
                                    const isRecordPayment = transition?.name?.toLowerCase().includes('record payment');
                                    if (isRecordPayment && order?.invoice_status === 'VOID') return false;

                                    // Hide "Generate Invoice" when an active (non-VOID) invoice already exists for this service order
                                    const transitionNameLc = transition?.name?.toLowerCase() || '';
                                    const toStateNameLc = getTransitionToStateNameLc(transition);
                                    const isGenerateInvoice = transitionNameLc.includes('generate invoice') ||
                                        transitionNameLc.includes('create invoice') ||
                                        toStateNameLc === 'invoiced';

                                    const invoiceExists = Boolean(order?.invoice_id);
                                    const isInvoiceVoid = order?.invoice_status === 'VOID';

                                    if (isGenerateInvoice && invoiceExists && !isInvoiceVoid) {
                                        return false;
                                    }

                                    return true;
                                })
                                .map((transition) => {
                                    const isCompleting = getTransitionToStateNameLc(transition) === 'completed' ||
                                        transition?.name?.toLowerCase().includes('complete');
                                    const isDisabled = transitionLoading || (isCompleting && !canComplete);

                                    const tooltipTitle = isCompleting && !canComplete
                                        ? (() => {
                                            const missingReports = completionRequirements?.missing_reports || [];
                                            const flashMissing = missingReports.some(report => report.includes('Flash Report'));
                                            const detailedMissing = missingReports.some(report => report.includes('Detailed Report'));
                                            const subOrdersMissing = missingReports.some(report => report.includes('Incomplete sub-orders'));
                                            const incompleteSubOrders = completionRequirements?.incomplete_sub_orders || [];

                                            const reasons = [];
                                            if (flashMissing && detailedMissing) {
                                                reasons.push('Create and complete both Flash Report and Detailed Report');
                                            } else if (flashMissing) {
                                                reasons.push('Create and complete Flash Report');
                                            } else if (detailedMissing) {
                                                reasons.push('Create and complete Detailed Report');
                                            }
                                            if (subOrdersMissing && incompleteSubOrders.length > 0) {
                                                reasons.push(`Complete sub-orders: ${incompleteSubOrders.slice(0, 3).join(', ')}${incompleteSubOrders.length > 3 ? ` (+${incompleteSubOrders.length - 3} more)` : ''}`);
                                            }

                                            return reasons.length > 0
                                                ? `Cannot complete: ${reasons.join('; ')}`
                                                : 'Cannot complete: Complete required reports';
                                        })()
                                        : transition.name;

                                    return (
                                        <Tooltip key={transition.id} title={tooltipTitle} arrow>
                                            <span>
                                                <PrimaryButton
                                                    onClick={() => handleMoveToNextStage(transition)}
                                                    disabled={isDisabled}
                                                    sx={{
                                                        px: 3,
                                                        py: 1.5,
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    {transitionLoading && selectedTransition?.id === transition.id ? (
                                                        <CircularProgress size={24} color="inherit" />
                                                    ) : (
                                                        transition.name
                                                    )}
                                                </PrimaryButton>
                                            </span>
                                        </Tooltip>
                                    );
                                })}
                            {/* Reissue Invoice — shown inside Available Actions when invoice is VOID */}
                            {order?.invoice_status === 'VOID' && permissions?.invoicing?.add && (
                                <PrimaryButton
                                    onClick={async () => {
                                        try {
                                            // If parent SO has sub-service orders, show job selection dialog
                                            if (subServiceOrders.length > 0) {
                                                setJobSelectionDialogOpen(true);
                                                return;
                                            }
                                            // No sub-orders — show view preferences dialog
                                            setViewPrefsDialogOpen(true);
                                        } catch (err) {
                                            toast.error(err.response?.data?.error || 'Failed to create invoice');
                                        }
                                    }}
                                >
                                    Reissue Invoice
                                </PrimaryButton>
                            )}
                            {/* Create Sub-Service Order — only while order is still active (before Completed) */}
                            {!order?.parent_order && user?.role?.permissions?.service_orders?.add === true &&
                             !['completed', 'invoiced', 'paid', 'closed', 'cancelled'].includes((order?.current_state?.name || '').toLowerCase()) && (
                                <PrimaryButton
                                    startIcon={<AddIcon />}
                                    onClick={handleCreateSubServiceOrder}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Create Sub-Service Order
                                </PrimaryButton>
                            )}
                            {availableTransitions.filter(transition => canPerformTransition(transition)).length === 0 && !(order?.invoice_status === 'VOID' && permissions?.invoicing?.add) && !(!order?.parent_order && user?.role?.permissions?.service_orders?.add === true && !['completed', 'invoiced', 'paid', 'closed', 'cancelled'].includes((order?.current_state?.name || '').toLowerCase())) && (
                                <Typography variant="body2" color={transitionsBlockedReason ? 'error' : 'text.secondary'} sx={{ fontStyle: 'italic' }}>
                                    {transitionsBlockedReason || 'No actions available for your role at this stage.'}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* Active Invoice Panel */}
            {order?.invoice_id && permissions?.invoicing?.view && (
                <InvoicePanel key={`active-${order.invoice_id}-${invoicePanelKey}`} invoiceId={order.invoice_id} onUpdate={fetchOrder} />
            )}

            {/* Void Invoice History */}
            {order?.void_invoice_ids?.length > 0 && permissions?.invoicing?.view && (
                order.void_invoice_ids.map(vid => (
                    <InvoicePanel key={`void-${vid}`} invoiceId={vid} onUpdate={fetchOrder} />
                ))
            )}


            {/* Completion Requirements Display */}
            {completionRequirements && !completionRequirements.can_complete && (
                completionRequirements.is_parent_order
                    ? completionRequirements.incomplete_sub_orders?.length > 0
                    : (completionRequirements.flash_report?.has_reports || completionRequirements.detailed_report?.has_reports || completionRequirements.incomplete_sub_orders?.length > 0)
            ) && (
                <Paper sx={{
                    p: 2,
                    mb: 3,
                    backgroundColor: '#fffbeb',
                    border: '1px solid',
                    borderColor: '#fcd34d',
                    borderRadius: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{ color: '#d97706', mt: 0.25 }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#92400e', mb: 1 }}>
                                Completion Requirements
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
                                {/* Flash / Detailed report chips — only for non-parent orders */}
                                {!completionRequirements.is_parent_order && (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#78350f' }}>
                                                Flash Report{completionRequirements.flash_report?.is_optional ? ' (Optional)' : ''}:
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={completionRequirements.flash_report?.has_completed ? 'completed' : (completionRequirements.flash_report?.is_optional ? 'optional' : completionRequirements.flash_report?.status)}
                                                color={completionRequirements.flash_report?.has_completed ? 'success' : (completionRequirements.flash_report?.is_optional ? 'default' : 'warning')}
                                                sx={{ fontWeight: 500 }}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#78350f' }}>Detailed Report:</Typography>
                                            <Chip
                                                size="small"
                                                label={completionRequirements.detailed_report.status}
                                                color={completionRequirements.detailed_report.has_completed ? 'success' : 'warning'}
                                                sx={{ fontWeight: 500 }}
                                            />
                                        </Box>
                                    </>
                                )}
                                {completionRequirements.incomplete_sub_orders?.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#78350f' }}>Sub-Orders:</Typography>
                                        <Chip
                                            size="small"
                                            label={`${completionRequirements.incomplete_sub_orders.length} incomplete`}
                                            color="warning"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Typography variant="body2" sx={{ color: '#92400e' }}>
                                {completionRequirements.is_parent_order
                                    ? `Complete all ${completionRequirements.incomplete_sub_orders.length} sub-service order${completionRequirements.incomplete_sub_orders.length > 1 ? 's' : ''} to finish this service order.`
                                    : (() => {
                                        const missingReports = completionRequirements.missing_reports || [];
                                        const flashMissing = missingReports.some(r => r.includes('Flash Report'));
                                        const detailedMissing = missingReports.some(r => r.includes('Detailed Report'));
                                        const incompleteSubOrders = completionRequirements.incomplete_sub_orders || [];
                                        const reasons = [];
                                        if (flashMissing && detailedMissing) reasons.push('complete both Flash Report and Detailed Report');
                                        else if (flashMissing) reasons.push('complete Flash Report');
                                        else if (detailedMissing) reasons.push('complete Detailed Report');
                                        if (incompleteSubOrders.length > 0) reasons.push(`complete ${incompleteSubOrders.length} sub-service order${incompleteSubOrders.length > 1 ? 's' : ''}`);
                                        return reasons.length > 0
                                            ? `Please ${reasons.join(' and ')} to finish this service order.`
                                            : 'Complete required items to finish this service order.';
                                    })()
                                }
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Transition Confirmation Dialog */}
            <Dialog
                open={transitionDialogOpen}
                onClose={() => setTransitionDialogOpen(false)}
            >
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to {selectedTransition?.name.toLowerCase()}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton onClick={() => setTransitionDialogOpen(false)}>Cancel</SecondaryButton>
                    <PrimaryButton
                        onClick={handleMoveToNextStage}
                        disabled={transitionLoading}
                    >
                        {transitionLoading ? <CircularProgress size={24} /> : 'Confirm'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            {/* Resource Release Warning Dialog */}
            <Dialog
                open={showResourceWarning}
                onClose={() => setShowResourceWarning(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}>
                    <WarningIcon color="primary" />
                    <Typography variant="h6" component="span" sx={{ color: 'text.primary' }}>
                        Resource Release Warning
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {transitionImpact?.warning_message || 'This action will release resources assigned to this service order.'}
                    </Typography>

                    {transitionImpact?.impact && (
                        <Box sx={{
                            bgcolor: `${theme.palette.primary.main}14`,
                            p: 2,
                            borderRadius: 2,
                            mb: 2,
                            border: '1px solid',
                            borderColor: 'primary.light'
                        }}>
                            <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                Resources to be released:
                            </Typography>

                            {transitionImpact.impact.will_release_equipment && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="body2" color="text.primary">
                                        <strong>Equipment:</strong> {transitionImpact.impact.equipment_count} item(s) will be released and made available for other service orders
                                    </Typography>
                                </Box>
                            )}

                            {transitionImpact.impact.will_release_consumables && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="body2" color="text.primary">
                                        <strong>Consumables:</strong> {transitionImpact.impact.consumables_count} item(s) will be released and added back to inventory
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        Please confirm that you want to proceed with this action. The resources will be immediately available for assignment to other service orders.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    px: 3,
                    pb: 2,
                    gap: 1,
                    justifyContent: 'flex-end'
                }}>
                    <SecondaryButton
                        onClick={() => {
                            setShowResourceWarning(false);
                            setTransitionImpact(null);
                        }}
                        sx={{ borderRadius: 2 }}
                    >
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        onClick={() => {
                            const toStateId = selectedTransition?.to_state?.id || selectedTransition?.to_state;
                            performTransition(selectedTransition, toStateId, 'Resources released due to state transition');
                        }}
                        disabled={transitionLoading}
                        sx={{ borderRadius: 2 }}
                    >
                        {transitionLoading ? <CircularProgress size={24} /> : 'Release resources & continue'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            {/* ── Full-width Content Layout ─────────────── */}
            <Box>

                {/* Basic Information Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        color: 'primary.main',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        pb: 1
                    }}>
                        Basic Information
                    </Typography>
                    <Paper sx={{ p: 3, borderRadius: 4 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Service Type
                                </Typography>
                                <Box sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {order.service_type ? (
                                        <Chip label={order.service_type.name} color="primary" variant="outlined" />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">No service type assigned</Typography>
                                    )}
                                </Box>
                                {/* Rate Card Information */}
                                {order.service_type?.current_rate_card && (
                                    <Box sx={{ mt: 2, p: 2, bgcolor: `${theme.palette.primary.main}14`, borderRadius: 2, border: '1px solid', borderColor: 'primary.light' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                                            Pricing Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">Price</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    ${order.service_type.current_rate_card.price} {order.service_type.current_rate_card.currency}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">Duration</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {order.service_type.current_rate_card.duration} hours
                                                </Typography>
                                            </Grid>
                                            {order.service_type.current_rate_card.discount_tier > 0 && (
                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">Discount</Typography>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {order.service_type.current_rate_card.discount_tier}%
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {order.service_type.current_rate_card.effective_from && (
                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">Valid From</Typography>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {formatDateOnly(order.service_type.current_rate_card.effective_from)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Job Environment
                                </Typography>
                                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                    {order.job_environment?.name || 'Not assigned'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Company
                                </Typography>
                                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                    {order.company
                                        ? `${order.company.name} (${order.company.code})`
                                        : 'Not assigned'}
                                </Typography>
                            </Grid>
                            {order.contact_person && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Contact Person
                                    </Typography>
                                    <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                        {order.contact_person && (order.contact_person.first_name || order.contact_person.last_name)
                                            ? `${order.contact_person.first_name || ''} ${order.contact_person.last_name || ''}`.trim()
                                            : 'No contact person assigned'} {order.contact_person?.email ? `(${order.contact_person.email})` : ''}
                                    </Typography>
                                </Grid>
                            )}
                            {order.sub_contractor && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Sub-Contractor
                                    </Typography>
                                    <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                        {order.sub_contractor}
                                    </Typography>
                                </Grid>
                            )}
                            {order.component_name && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Component Name
                                    </Typography>
                                    <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                        {order.component_name}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    REQ ID
                                </Typography>
                                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                    {order.req_id || 'Not assigned'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Description
                                </Typography>
                                <Typography variant="body1" sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, minHeight: '60px' }}>
                                    {order.description}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>

                {/* Sub-Service Orders Section - Only show if this is NOT a sub-service order */}
                {order && !order.parent_order && (
                    <Box id="sub-service-orders" sx={{ mb: 3, scrollMarginTop: '80px' }}>
                        <Box sx={{
                            mb: 2,
                            borderBottom: '2px solid',
                            borderColor: 'secondary.main',
                            pb: 1
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: 'secondary.main',
                            }}>
                                Sub-Service Orders
                                {subServiceOrders.length > 0 && ` (${subServiceOrders.length})`}
                            </Typography>
                        </Box>
                        <Paper sx={{ p: 3, borderRadius: 4 }}>
                            {loadingSubOrders ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}>
                                    <CircularProgress size={40} />
                                    <Typography variant="body2" color="text.secondary">Loading sub-orders…</Typography>
                                </Box>
                            ) : subServiceOrders.length > 0 ? (
                                <>
                                    {/* Search and Filter Controls */}
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                        <TextField
                                            size="small"
                                            placeholder="Search by name or component..."
                                            value={subOrderSearch}
                                            onChange={(e) => { setSubOrderSearch(e.target.value); setSubOrderPage(0); }}
                                            sx={{ minWidth: 220 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <FormControl size="small" sx={{ minWidth: 140 }}>
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                value={subOrderStatusFilter}
                                                label="Status"
                                                onChange={(e) => { setSubOrderStatusFilter(e.target.value); setSubOrderPage(0); }}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {[...new Set(subServiceOrders.map(r => r.child_order_status).filter(Boolean))].map(status => (
                                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl size="small" sx={{ minWidth: 160 }}>
                                            <InputLabel>Service Type</InputLabel>
                                            <Select
                                                value={subOrderServiceTypeFilter}
                                                label="Service Type"
                                                onChange={(e) => { setSubOrderServiceTypeFilter(e.target.value); setSubOrderPage(0); }}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {[...new Set(subServiceOrders.map(r => r.child_order_service_type).filter(Boolean))].map(type => (
                                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    {/* Filtered List */}
                                    {(() => {
                                        const filteredOrders = subServiceOrders.filter(relationship => {
                                            const searchLower = subOrderSearch.toLowerCase();
                                            const matchesSearch = !subOrderSearch ||
                                                (relationship.child_order_name || '').toLowerCase().includes(searchLower) ||
                                                (relationship.child_order_component_name || '').toLowerCase().includes(searchLower);
                                            const matchesStatus = !subOrderStatusFilter || relationship.child_order_status === subOrderStatusFilter;
                                            const matchesType = !subOrderServiceTypeFilter || relationship.child_order_service_type === subOrderServiceTypeFilter;
                                            return matchesSearch && matchesStatus && matchesType;
                                        });
                                        const paginatedOrders = filteredOrders.slice(
                                            subOrderPage * subOrderRowsPerPage,
                                            subOrderPage * subOrderRowsPerPage + subOrderRowsPerPage
                                        );
                                        return (
                                            <>
                                                {paginatedOrders.length > 0 ? (
                                                    <List>
                                                        {paginatedOrders.map((relationship) => {
                                                            const childOrderId = relationship.child_order_id;
                                                            const displayName = relationship.child_order_name || `SO-${childOrderId}`;
                                                            const childNavId = displayName || childOrderId;
                                                            return (
                                                                <ListItem
                                                                    key={relationship.id}
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                        borderRadius: 2,
                                                                        mb: 1,
                                                                        '&:hover': {
                                                                            backgroundColor: 'action.hover',
                                                                        }
                                                                    }}
                                                                >
                                                                    <ListItemText
                                                                        primaryTypographyProps={{ component: 'div' }}
                                                                        secondaryTypographyProps={{ component: 'div' }}
                                                                        primary={
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                                    {displayName}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={relationship.child_order_service_type}
                                                                                    size="small"
                                                                                    color="primary"
                                                                                    variant="outlined"
                                                                                />
                                                                                <Chip
                                                                                    label={relationship.child_order_status}
                                                                                    size="small"
                                                                                    color={
                                                                                        relationship.child_order_status === 'Completed' ? 'success' :
                                                                                        relationship.child_order_status === 'InProgress' ? 'warning' :
                                                                                        relationship.child_order_status === 'New' ? 'info' : 'default'
                                                                                    }
                                                                                />
                                                                            </Box>
                                                                        }
                                                                        secondary={
                                                                            <Box>
                                                                                {relationship.child_order_component_name && (
                                                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                                        Component: {relationship.child_order_component_name}
                                                                                    </Typography>
                                                                                )}
                                                                                {relationship.child_order_scheduled_at && (
                                                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                                        Scheduled: {new Date(relationship.child_order_scheduled_at).toLocaleString()}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        }
                                                                    />
                                                                    <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                                                        <Chip
                                                                            icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                                                            label="View"
                                                                            size="small"
                                                                            onClick={() => navigate(`/service-orders/${childNavId}`)}
                                                                            clickable
                                                                            color="primary"
                                                                            variant="outlined"
                                                                            sx={{ fontSize: '0.75rem' }}
                                                                        />
                                                                        <Chip
                                                                            icon={<EditIcon sx={{ fontSize: 16 }} />}
                                                                            label="Edit"
                                                                            size="small"
                                                                            onClick={() => navigate(`/service-orders/${childNavId}/edit`)}
                                                                            clickable
                                                                            color="primary"
                                                                            variant="outlined"
                                                                            sx={{ fontSize: '0.75rem' }}
                                                                        />
                                                                    </Box>
                                                                </ListItem>
                                                            );
                                                        })}
                                                    </List>
                                                ) : (
                                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            No sub-service orders match your filters
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {filteredOrders.length > subOrderRowsPerPage && (
                                                    <TablePagination
                                                        component="div"
                                                        count={filteredOrders.length}
                                                        page={subOrderPage}
                                                        onPageChange={(e, newPage) => setSubOrderPage(newPage)}
                                                        rowsPerPage={subOrderRowsPerPage}
                                                        onRowsPerPageChange={(e) => {
                                                            setSubOrderRowsPerPage(parseInt(e.target.value, 10));
                                                            setSubOrderPage(0);
                                                        }}
                                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                                    />
                                                )}
                                            </>
                                        );
                                    })()}
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No sub-service orders created yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Sub-service orders will appear here once created for this service order
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>
                )}

                {/* Assignment & Scheduling Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        color: 'primary.main',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        pb: 1
                    }}>
                        Assignment & Scheduling
                    </Typography>
                    <Paper sx={{ p: 3, borderRadius: 4 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Technician
                                </Typography>
                                <Box sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                    {order.technician ? (
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                {order.technician.first_name || order.technician.last_name
                                                    ? `${order.technician.first_name || ''} ${order.technician.last_name || ''}`.trim()
                                                    : order.technician.email}
                                            </Typography>
                                            {order.technician.position && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {order.technician.position}
                                                </Typography>
                                            )}
                                            {order.technician.department && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {order.technician.department}
                                                </Typography>
                                            )}
                                            {order.technician.phone && (
                                                <Typography variant="body2" color="text.secondary">
                                                    📞 {order.technician.phone}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography variant="body1">Not assigned</Typography>
                                    )}
                                </Box>
                            </Grid>
                            {!order?.parent_order && (
                                <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Job Lead Technician
                                    </Typography>
                                    <Box sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                        {order.job_lead_technician ? (
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                    {order.job_lead_technician.first_name || order.job_lead_technician.last_name
                                                        ? `${order.job_lead_technician.first_name || ''} ${order.job_lead_technician.last_name || ''}`.trim()
                                                        : order.job_lead_technician.email}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    May edit any report within this job
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body1">Not assigned</Typography>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Site Location
                                </Typography>
                                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                    {order.site_location?.name || 'Not specified'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Scheduled Date
                                </Typography>
                                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                    {formatDateTime(order.scheduled_at)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Technician Credentials Section */}
                    {order.technician && (order.technician.credentials?.length > 0 || (Array.isArray(order.technician.skills) && order.technician.skills.length > 0) || (Array.isArray(order.technician.certifications) && order.technician.certifications.length > 0)) && (
                        <Box sx={{ mb: 3, mt: 3 }}>
                            <Typography variant="h6" sx={{
                                mb: 2,
                                fontWeight: 'bold',
                                color: 'primary.main',
                                borderBottom: '2px solid',
                                borderColor: 'primary.main',
                                pb: 1
                            }}>
                                Technician Credentials & Qualifications
                            </Typography>
                            <Paper sx={{ p: 3, borderRadius: 4 }}>
                                <Grid container spacing={3}>
                                    {/* Skills Section */}
                                    {order.technician.skills && Array.isArray(order.technician.skills) && order.technician.skills.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Skills
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {order.technician.skills.map((skill, index) => {
                                                    let skillLabel = 'Unknown Skill';
                                                    try {
                                                        if (typeof skill === 'string') {
                                                            skillLabel = skill;
                                                        } else if (skill && typeof skill === 'object') {
                                                            skillLabel = skill.name || skill.title || skill.label || 'Unnamed Skill';
                                                        } else {
                                                            skillLabel = String(skill);
                                                        }
                                                    } catch (error) {
                                                        console.warn('Error processing skill:', skill, error);
                                                        skillLabel = 'Invalid Skill Data';
                                                    }
                                                    return (
                                                        <Chip
                                                            key={index}
                                                            label={skillLabel}
                                                            color="primary"
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Grid>
                                    )}

                                    {/* Certifications Section */}
                                    {order.technician.certifications && Array.isArray(order.technician.certifications) && order.technician.certifications.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Certifications
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {order.technician.certifications.map((cert, index) => {
                                                    let certLabel = 'Unknown Certification';
                                                    try {
                                                        if (typeof cert === 'string') {
                                                            certLabel = cert;
                                                        } else if (cert && typeof cert === 'object') {
                                                            certLabel = cert.name || cert.title || cert.label || 'Unnamed Certification';
                                                        } else {
                                                            certLabel = String(cert);
                                                        }
                                                    } catch (error) {
                                                        console.warn('Error processing certification:', cert, error);
                                                        certLabel = 'Invalid Certification Data';
                                                    }
                                                    return (
                                                        <Chip
                                                            key={index}
                                                            label={certLabel}
                                                            color="success"
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Grid>
                                    )}

                                    {/* Credential Documents Section */}
                                    {order.technician.credentials && order.technician.credentials.length > 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                Credential Documents
                                            </Typography>
                                            <DocumentDisplay
                                                documents={order.technician.credentials}
                                                entityType="technician"
                                                showMetadata={true}
                                                maxHeight="300px"
                                                entityData={{
                                                    name: `${order.technician.first_name || ''} ${order.technician.last_name || ''}`.trim() || order.technician.email,
                                                    email: order.technician.email,
                                                    phone: order.technician.phone
                                                }}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        </Box>
                    )}
                </Box>

                <ServiceOrderCrewSection serviceOrder={order} />

                {/* Additional Information Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        color: 'primary.main',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        pb: 1
                    }}>
                        Additional Information
                    </Typography>
                    <Paper sx={{ p: 3, borderRadius: 4 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Instructions
                                </Typography>
                                <Typography variant="body1" sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, minHeight: '60px' }}>
                                    {order.instructions || 'No instructions provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Special Remarks
                                </Typography>
                                <Typography variant="body1" sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, minHeight: '60px' }}>
                                    {order.remarks || 'No remarks provided'}
                                </Typography>
                            </Grid>
                            {order.customer_comments && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Customer Comments
                                    </Typography>
                                    <Typography variant="body1" sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, minHeight: '60px' }}>
                                        {order.customer_comments}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Box>

                {/* Purchase Order Section */}
                {!order?.parent_order && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{
                            mb: 2,
                            fontWeight: 'bold',
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                            pb: 1
                        }}>
                            Purchase Order
                        </Typography>
                        {(() => {
                            const poCardStatus = getPoStatus(order.po_expiry_date, order.po_number);
                            return (
                                <Paper sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    border: poCardStatus.isExpired
                                        ? '1.5px solid #fca5a5'
                                        : (poCardStatus.isExpiringSoon ? '1.5px solid #ed6c02' : '1px solid rgba(0,0,0,0.08)'),
                                    backgroundColor: poCardStatus.isExpired
                                        ? '#fef2f2'
                                        : (poCardStatus.isExpiringSoon ? '#fffdf7' : 'background.paper'),
                                }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                PO Number
                                            </Typography>
                                            <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                                {order.po_number || 'Not specified'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                PO Date
                                            </Typography>
                                            <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                                {order.po_date ? formatDateOnly(order.po_date) : 'Not specified'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                                    PO Expiry / Due Date
                                                </Typography>
                                                {poCardStatus.isExpired && (
                                                    <Chip
                                                        label="EXPIRED"
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            fontWeight: 'bold',
                                                            backgroundColor: '#fee2e2',
                                                            color: '#b91c1c',
                                                            border: '1px solid #fca5a5'
                                                        }}
                                                    />
                                                )}
                                                {poCardStatus.isExpiringSoon && (
                                                    <Chip label="EXPIRING SOON" color="warning" size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} />
                                                )}
                                            </Box>
                                            <Typography variant="body1" sx={{
                                                p: 1,
                                                backgroundColor: poCardStatus.isExpired ? '#fee2e2' : (poCardStatus.isExpiringSoon ? '#fff8e1' : 'grey.50'),
                                                color: poCardStatus.isExpired ? '#991b1b' : (poCardStatus.isExpiringSoon ? 'warning.dark' : 'text.primary'),
                                                fontWeight: (poCardStatus.isExpired || poCardStatus.isExpiringSoon) ? 600 : 400,
                                                borderRadius: 1
                                            }}>
                                                {order.po_expiry_date ? `${formatDateOnly(order.po_expiry_date)} (${poCardStatus.description})` : 'Not specified'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            );
                        })()}
                    </Box>
                )}

                {/* Documents Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        color: 'primary.main',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        pb: 1
                    }}>
                        Documents
                    </Typography>
                    <Paper sx={{ borderRadius: 4, overflow: 'hidden' }}>
                        <DocumentDisplay
                            documents={order.documents || []}
                            title="Service Order Documents"
                            showUploadButton={canUploadInvoice()}
                            onUploadClick={handleUploadClick}
                            onRefresh={fetchOrder}
                        />
                    </Paper>
                </Box>

                {/* Parent Service Order Section (if this is a sub-service order) */}
                {order?.parent_order && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{
                            mb: 2,
                            fontWeight: 'bold',
                            color: 'info.main',
                            borderBottom: '2px solid',
                            borderColor: 'info.main',
                            pb: 1
                        }}>
                            Parent Service Order
                        </Typography>
                        <Paper sx={{ p: 3, borderRadius: 4, backgroundColor: `${theme.palette.info.main}0a` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        {order.parent_order.display || (order.parent_order.name ? order.parent_order.name : `SO-${order.parent_order.id}`) + (order.parent_order.service_type ? ` - ${order.parent_order.service_type}` : '')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {order.parent_order.description || 'No description'}
                                    </Typography>
                                </Box>
                                <SecondaryButton
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => navigate(`/service-orders/${order.parent_order.display_name || order.parent_order.id}`)}
                                    sx={{ ml: 2 }}
                                >
                                    View parent
                                </SecondaryButton>
                            </Box>
                        </Paper>
                    </Box>
                )}

                {/* Reports Section */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        pb: 1
                    }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 'bold',
                            color: 'primary.main',
                        }}>
                            Reports
                        </Typography>
                        {user?.role?.permissions?.reports?.add === true && (
                            <PrimaryButton
                                startIcon={<AddIcon />}
                                size="small"
                                onClick={() => toast.info('Report creation functionality coming soon!')}
                            >
                                Create report
                            </PrimaryButton>
                        )}
                    </Box>
                    <Paper sx={{ borderRadius: 4, overflow: 'hidden', p: 2 }}>
                        {order.reports && order.reports.total_count > 0 ? (
                            <Box>
                                {/* Flash Reports */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                                        Flash Reports {order.reports.flash_reports && order.reports.flash_reports.length > 0 ? `(${order.reports.flash_reports.length})` : ''}
                                    </Typography>

                                    {order.reports.flash_reports && order.reports.flash_reports.length > 0 ? (
                                        <List dense>
                                            {order.reports.flash_reports.map((report) => (
                                                <ListItem key={report.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                                                    <ListItemText
                                                        primaryTypographyProps={{ component: 'div' }}
                                                        secondaryTypographyProps={{ component: 'div' }}
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Chip
                                                                        label={report.status_display}
                                                                        size="small"
                                                                        color={report.status === 'approved' ? 'success' : report.status === 'completed' ? 'primary' : 'default'}
                                                                        variant="outlined"
                                                                    />
                                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                        {report.title}
                                                                    </Typography>
                                                                    {report.report_version && (
                                                                        <Chip
                                                                            label={`v${report.report_version}`}
                                                                            size="small"
                                                                            color="secondary"
                                                                            variant="filled"
                                                                            sx={{ fontSize: '0.65rem', height: '20px' }}
                                                                        />
                                                                    )}
                                                                </Box>

                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box>
                                                                <Typography variant="caption" display="block">
                                                                    Examiner: {report.examiner_name} | NDE Method: {report.nde_method}
                                                                </Typography>
                                                                <Typography variant="caption" display="block">
                                                                    Inspection Date: {formatDateOnly(report.inspection_date)}
                                                                </Typography>

                                                                {/* Observations Section */}
                                                                {report.observations && report.observations.length > 0 && (
                                                                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                                            🔍 Observations ({report.observations.length})
                                                                        </Typography>
                                                                        {report.observations.map((obs, idx) => (
                                                                            <Box key={idx} sx={{ mb: 0.5, pl: 1 }}>
                                                                                <Typography variant="caption" display="block">
                                                                                    • {obs.observation}
                                                                                </Typography>

                                                                                {/* Show attached images directly under each observation */}
                                                                                {obs.images && obs.images.length > 0 && (
                                                                                    <Box sx={{ mt: 0.5, pl: 1 }}>
                                                                                        {obs.images.map((image, imgIdx) => (
                                                                                            <Box key={imgIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                                                                <Typography variant="caption" sx={{ color: 'info.main', fontStyle: 'italic' }}>
                                                                                                    📷 {image.name || image.original_filename || 'Image'}
                                                                                                </Typography>
                                                                                                <Chip
                                                                                                    icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                                                                    label="View"
                                                                                                    size="small"
                                                                                                    onClick={() => {
                                                                                                        // Try to get URL from Document model first
                                                                                                        if (image.document_id && report.documents) {
                                                                                                            const matchingDoc = report.documents.find(doc => doc.id === image.document_id);
                                                                                                            if (matchingDoc) {
                                                                                                                const viewUrl = matchingDoc.url || matchingDoc.presigned_url || matchingDoc.view_url;
                                                                                                                if (viewUrl) {
                                                                                                                    window.open(viewUrl, '_blank');
                                                                                                                    return;
                                                                                                                }
                                                                                                            }
                                                                                                        }

                                                                                                        // Fallback to direct image URL
                                                                                                        if (image.url) {
                                                                                                            window.open(image.url, '_blank');
                                                                                                        } else if (image.s3_key) {
                                                                                                            toast.info('Image URL not available. Please refresh the page.');
                                                                                                        } else {
                                                                                                            toast.info('Image URL not available');
                                                                                                        }
                                                                                                    }}
                                                                                                    clickable
                                                                                                    color="primary"
                                                                                                    variant="outlined"
                                                                                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                                                                                />
                                                                                                <Chip
                                                                                                    icon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                                                                    label="Download"
                                                                                                    size="small"
                                                                                                    onClick={() => {
                                                                                                        // Try to get download URL from Document model first
                                                                                                        if (image.document_id && report.documents) {
                                                                                                            const matchingDoc = report.documents.find(doc => doc.id === image.document_id);
                                                                                                            if (matchingDoc) {
                                                                                                                const downloadUrl = matchingDoc.download_url || matchingDoc.url || matchingDoc.presigned_url;
                                                                                                                const filename = matchingDoc.original_filename || matchingDoc.filename || image.name || 'observation_document';
                                                                                                                if (downloadUrl) {
                                                                                                                    const link = document.createElement('a');
                                                                                                                    link.href = downloadUrl;
                                                                                                                    link.download = filename;
                                                                                                                    link.click();
                                                                                                                    return;
                                                                                                                }
                                                                                                            }
                                                                                                        }

                                                                                                        // Fallback to direct download URL
                                                                                                        if (image.download_url) {
                                                                                                            const link = document.createElement('a');
                                                                                                            link.href = image.download_url;
                                                                                                            link.download = image.name || image.original_filename || 'observation_document';
                                                                                                            link.click();
                                                                                                        } else if (image.s3_key) {
                                                                                                            toast.info('Download URL not available. Please refresh the page.');
                                                                                                        } else {
                                                                                                            toast.info('Download URL not available');
                                                                                                        }
                                                                                                    }}
                                                                                                    clickable
                                                                                                    color="success"
                                                                                                    variant="outlined"
                                                                                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                                                                                />
                                                                                            </Box>
                                                                                        ))}
                                                                                    </Box>
                                                                                )}

                                                                                {/* Legacy support for image_name field */}
                                                                                {(!obs.images || obs.images.length === 0) && obs.image_name && (
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                                                        <Typography variant="caption" sx={{ color: 'info.main', fontStyle: 'italic' }}>
                                                                                            📷 {obs.image_name}
                                                                                        </Typography>
                                                                                        <Chip
                                                                                            icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                                                            label="View"
                                                                                            size="small"
                                                                                            onClick={() => {
                                                                                                // Check if observation has images with Document objects (new structure)
                                                                                                if (obs.images && obs.images.length > 0) {
                                                                                                    const firstImage = obs.images[0];

                                                                                                    if (firstImage.document_id) {
                                                                                                        // This observation has a linked document
                                                                                                        if (report.documents && report.documents.length > 0) {
                                                                                                            const matchingDoc = report.documents.find(doc => doc.id === firstImage.document_id);
                                                                                                            if (matchingDoc) {
                                                                                                                const docUrl = matchingDoc.presigned_url || matchingDoc.url;
                                                                                                                if (docUrl) {
                                                                                                                    window.open(docUrl, '_blank');
                                                                                                                    return;
                                                                                                                }
                                                                                                            }
                                                                                                        }
                                                                                                    }

                                                                                                    // If no document found, try direct URL
                                                                                                    if (firstImage.url) {
                                                                                                        window.open(firstImage.url, '_blank');
                                                                                                        return;
                                                                                                    }
                                                                                                }

                                                                                                // Primary approach: Use flash report documents
                                                                                                if (report.documents && report.documents.length > 0) {
                                                                                                    let docUrl = null;

                                                                                                    // Try to find a document that matches the observation
                                                                                                    if (obs.image_name) {
                                                                                                        // Try to match by filename
                                                                                                        const matchingDoc = report.documents.find(doc =>
                                                                                                            doc.original_filename === obs.image_name ||
                                                                                                            doc.filename === obs.image_name ||
                                                                                                            doc.description?.toLowerCase().includes(obs.image_name?.toLowerCase())
                                                                                                        );

                                                                                                        if (matchingDoc) {
                                                                                                            docUrl = matchingDoc.presigned_url || matchingDoc.url;
                                                                                                        }
                                                                                                    }

                                                                                                    // If still no match, use the first available document
                                                                                                    if (!docUrl) {
                                                                                                        docUrl = report.documents[0].presigned_url || report.documents[0].url;
                                                                                                    }

                                                                                                    if (docUrl) {
                                                                                                        window.open(docUrl, '_blank');
                                                                                                    } else {
                                                                                                        toast.info('Document URL not available. Please refresh the page.');
                                                                                                    }
                                                                                                } else {
                                                                                                    // Fallback: Check observation's images field
                                                                                                    if (obs.images && obs.images.length > 0) {
                                                                                                        const firstImage = obs.images[0];

                                                                                                        if (firstImage.url) {
                                                                                                            window.open(firstImage.url, '_blank');
                                                                                                        } else if (firstImage.s3_key) {
                                                                                                            toast.info('Image URL not available. Please refresh the page.');
                                                                                                        } else {
                                                                                                            toast.info('Image URL not available');
                                                                                                        }
                                                                                                    } else {
                                                                                                        toast.info('No documents available for this observation');
                                                                                                    }
                                                                                                }
                                                                                            }}
                                                                                            clickable
                                                                                            color="primary"
                                                                                            variant="outlined"
                                                                                            sx={{ fontSize: '0.7rem', height: 22 }}
                                                                                        />
                                                                                        <Chip
                                                                                            icon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                                                            label="Download"
                                                                                            size="small"
                                                                                            onClick={() => {
                                                                                                // Check if observation has images with Document objects (new structure)
                                                                                                if (obs.images && obs.images.length > 0) {
                                                                                                    const firstImage = obs.images[0];

                                                                                                    if (firstImage.document_id) {
                                                                                                        // This observation has a linked document
                                                                                                        if (report.documents && report.documents.length > 0) {
                                                                                                            const matchingDoc = report.documents.find(doc => doc.id === firstImage.document_id);
                                                                                                            if (matchingDoc) {
                                                                                                                const downloadUrl = matchingDoc.download_url || matchingDoc.presigned_url || matchingDoc.url;
                                                                                                                if (downloadUrl) {
                                                                                                                    const link = document.createElement('a');
                                                                                                                    link.href = downloadUrl;
                                                                                                                    link.download = matchingDoc.original_filename || matchingDoc.filename || 'observation_document';
                                                                                                                    link.click();
                                                                                                                    return;
                                                                                                                }
                                                                                                            }
                                                                                                        }
                                                                                                    }

                                                                                                    // If no document found, try direct download URL
                                                                                                    if (firstImage.download_url) {
                                                                                                        const link = document.createElement('a');
                                                                                                        link.href = firstImage.download_url;
                                                                                                        link.download = firstImage.name || 'observation_document';
                                                                                                        link.click();
                                                                                                        return;
                                                                                                    }
                                                                                                }

                                                                                                // Primary approach: Use flash report documents
                                                                                                if (report.documents && report.documents.length > 0) {
                                                                                                    let downloadUrl = null;
                                                                                                    let filename = 'observation_document';

                                                                                                    // Try to find a document that matches the observation
                                                                                                    if (obs.image_name) {
                                                                                                        // Try to match by filename
                                                                                                        const matchingDoc = report.documents.find(doc =>
                                                                                                            doc.original_filename === obs.image_name ||
                                                                                                            doc.filename === obs.image_name ||
                                                                                                            doc.description?.toLowerCase().includes(obs.image_name?.toLowerCase())
                                                                                                        );

                                                                                                        if (matchingDoc) {
                                                                                                            downloadUrl = matchingDoc.download_url || matchingDoc.url;
                                                                                                            filename = matchingDoc.original_filename || matchingDoc.filename || 'observation_document';
                                                                                                        }
                                                                                                    }

                                                                                                    // If still no match, use the first available document
                                                                                                    if (!downloadUrl) {
                                                                                                        const firstDoc = report.documents[0];
                                                                                                        downloadUrl = firstDoc.download_url || firstDoc.presigned_url || firstDoc.url;
                                                                                                        filename = firstDoc.original_filename || firstDoc.filename || 'observation_document';
                                                                                                    }

                                                                                                    if (downloadUrl) {
                                                                                                        const link = document.createElement('a');
                                                                                                        link.href = downloadUrl;
                                                                                                        link.download = filename;
                                                                                                        link.click();
                                                                                                    } else {
                                                                                                        toast.info('Download URL not available. Please refresh the page.');
                                                                                                    }
                                                                                                } else {
                                                                                                    // Fallback: Check observation's images field
                                                                                                    if (obs.images && obs.images.length > 0) {
                                                                                                        const firstImage = obs.images[0];

                                                                                                        if (firstImage.download_url) {
                                                                                                            const link = document.createElement('a');
                                                                                                            link.href = firstImage.download_url;
                                                                                                            link.download = firstImage.name || 'observation_document';
                                                                                                            link.click();
                                                                                                        } else if (firstImage.s3_key) {
                                                                                                            toast.info('Download URL not available. Please refresh the page.');
                                                                                                        } else {
                                                                                                            toast.info('Download URL not available');
                                                                                                        }
                                                                                                    } else {
                                                                                                        toast.info('No documents available for download');
                                                                                                    }
                                                                                                }
                                                                                            }}
                                                                                            clickable
                                                                                            color="success"
                                                                                            variant="outlined"
                                                                                            sx={{ fontSize: '0.7rem', height: 22 }}
                                                                                        />
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                )}


                                                            </Box>
                                                        }
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        {/* Show View button - behavior depends on ui_visible permission */}
                                                        {user?.role?.permissions?.reporting?.view === true ? (
                                                            // User has full reporting access - navigate to report page
                                                            <Chip
                                                                icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                                                label="View"
                                                                size="small"
                                                                onClick={() => navigate(`/reports/flash/${report.id}`)}
                                                                clickable
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.75rem' }}
                                                            />
                                                        ) : (
                                                            // User without UI access - show PDF directly if available
                                                            <Chip
                                                                icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                                                label={report.has_pdf ? "View PDF" : "View"}
                                                                size="small"
                                                                onClick={() => {
                                                                    if (report.has_pdf && report.pdf_url) {
                                                                        window.open(report.pdf_url, '_blank');
                                                                    } else {
                                                                        toast.info('PDF not available yet for this report');
                                                                    }
                                                                }}
                                                                clickable
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.75rem' }}
                                                            />
                                                        )}
                                                        {/* Download PDF button when PDF is available */}
                                                        {report.has_pdf && report.pdf_download_url && (
                                                            <Chip
                                                                icon={<DownloadIcon sx={{ fontSize: 16 }} />}
                                                                label="Download"
                                                                size="small"
                                                                onClick={async () => {
                                                                    try {
                                                                        const reportType = report.report_type ? 'details-reports' : 'flash-reports';
                                                                        console.log(`📡 Calling ${reportType} download API for report ${report.id}`);
                                                                        const response = await api.get(`/api/service-orders/${reportType}/${report.id}/download/`);
                                                                        console.log('✅ Download response:', response.data);
                                                                        const downloadUrl = response.data?.download_url;
                                                                        if (downloadUrl) {
                                                                            console.log('🔗 Opening download URL');
                                                                            window.open(downloadUrl, '_blank');
                                                                        } else {
                                                                            toast.error('Download URL not available');
                                                                        }
                                                                    } catch (error) {
                                                                        console.error('❌ Download API error:', error);
                                                                        // Check for quota exceeded (429)
                                                                        if (error.response?.status === 429) {
                                                                            const message = error.response?.data?.error || 'Storage quota exceeded. Please contact support.';
                                                                            toast.error(message);
                                                                        } else {
                                                                            toast.error('Download failed: ' + (error.response?.data?.error || error.message));
                                                                        }
                                                                    }
                                                                }}
                                                                clickable
                                                                color="success"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.75rem' }}
                                                            />
                                                        )}
                                                        {/* Email button for completed detailed reports */}
                                                        {report.report_type && report.has_pdf && (
                                                            <Chip
                                                                icon={<EmailIcon sx={{ fontSize: 16 }} />}
                                                                label="Email"
                                                                size="small"
                                                                onClick={() => setEmailReportTarget({ id: report.id, title: report.report_no || report.report_type })}
                                                                clickable
                                                                color="info"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.75rem' }}
                                                            />
                                                        )}
                                                        {/* Edit button only for users with full UI access and change permission */}
                                                        {user?.role?.permissions?.reporting?.view === true && user?.role?.permissions?.reporting?.change === true && (
                                                            <Chip
                                                                icon={<EditIcon sx={{ fontSize: 16 }} />}
                                                                label="Edit"
                                                                size="small"
                                                                onClick={() => navigate(`/reports/flash/${report.id}/edit`)}
                                                                clickable
                                                                color="warning"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.75rem' }}
                                                            />
                                                        )}
                                                    </Box>
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Box sx={{ p: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                                No flash reports created yet
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                Flash reports will appear here once created for this service order
                                            </Typography>
                                        </Box>
                                    )}



                                    {/* Details Reports */}
                                    {order.reports.details_reports && order.reports.details_reports.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                                                Details Reports ({order.reports.details_reports.length})
                                            </Typography>
                                            <List dense>
                                                {order.reports.details_reports.map((report) => (
                                                    <ListItem key={report.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                                                        <ListItemText
                                                            primaryTypographyProps={{ component: 'div' }}
                                                            secondaryTypographyProps={{ component: 'div' }}
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Chip
                                                                            label={report.status_display}
                                                                            size="small"
                                                                            color={report.status === 'approved' ? 'success' : report.status === 'completed' ? 'primary' : 'default'}
                                                                            variant="outlined"
                                                                        />
                                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                            {report.title}
                                                                        </Typography>
                                                                        {report.report_version && (
                                                                            <Chip
                                                                                label={`v${report.report_version}`}
                                                                                size="small"
                                                                                color="secondary"
                                                                                variant="filled"
                                                                                sx={{ fontSize: '0.65rem', height: '20px' }}
                                                                            />
                                                                        )}
                                                                    </Box>

                                                                </Box>
                                                            }
                                                            secondary={
                                                                <Box>
                                                                    <Typography variant="caption" display="block">
                                                                        Type: {report.report_type} | Report Date: {formatDateOnly(report.report_date)}
                                                                    </Typography>
                                                                    <Typography variant="caption" display="block">
                                                                        Test Date: {formatDateOnly(report.test_date)}
                                                                    </Typography>

                                                                    {/* Observations Section - Same as Flash Reports */}
                                                                    {report.observations && report.observations.length > 0 && (
                                                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                                                🔍 Observations ({report.observations.length})
                                                                            </Typography>
                                                                            {report.observations.map((obs, idx) => {
                                                                                return (
                                                                                    <Box key={idx} sx={{ mb: 0.5, pl: 1 }}>
                                                                                        <Typography variant="caption" display="block">
                                                                                            • {obs.observation || obs.description}
                                                                                        </Typography>

                                                                                        {/* Show attached images directly under each observation */}
                                                                                        {(obs.images && obs.images.length > 0) || (obs.images_data && obs.images_data.length > 0) ? (
                                                                                            <Box sx={{ mt: 0.5, pl: 1 }}>
                                                                                                {/* Handle both obs.images (flash reports) and obs.images_data (detailed reports) */}
                                                                                                {(obs.images || obs.images_data || []).map((image, imgIdx) => (
                                                                                                    <Box key={imgIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                                                                        <Typography variant="caption" sx={{ color: 'info.main', fontStyle: 'italic' }}>
                                                                                                            📷 {image.name || image.original_filename || 'Image'}
                                                                                                        </Typography>
                                                                                                        <Chip
                                                                                                            icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                                                                            label="View"
                                                                                                            size="small"
                                                                                                            onClick={() => {
                                                                                                                // Try to get URL from Document model first
                                                                                                                if (image.document_id && report.documents) {
                                                                                                                    const matchingDoc = report.documents.find(doc => doc.id === image.document_id);
                                                                                                                    if (matchingDoc) {
                                                                                                                        const viewUrl = matchingDoc.url || matchingDoc.presigned_url || matchingDoc.view_url;
                                                                                                                        if (viewUrl) {
                                                                                                                            window.open(viewUrl, '_blank');
                                                                                                                            return;
                                                                                                                        }
                                                                                                                    }
                                                                                                                }

                                                                                                                // Fallback to direct image URL
                                                                                                                if (image.url) {
                                                                                                                    window.open(image.url, '_blank');
                                                                                                                } else if (image.s3_key) {
                                                                                                                    toast.info('Image URL not available. Please refresh the page.');
                                                                                                                } else {
                                                                                                                    toast.info('Image URL not available');
                                                                                                                }
                                                                                                            }}
                                                                                                            clickable
                                                                                                            color="primary"
                                                                                                            variant="outlined"
                                                                                                            sx={{ fontSize: '0.7rem', height: 22 }}
                                                                                                        />
                                                                                                        <Chip
                                                                                                            icon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                                                                            label="Download"
                                                                                                            size="small"
                                                                                                            onClick={() => {
                                                                                                                // Try to get download URL from Document model first
                                                                                                                if (image.document_id && report.documents) {
                                                                                                                    const matchingDoc = report.documents.find(doc => doc.id === image.document_id);
                                                                                                                    if (matchingDoc) {
                                                                                                                        const downloadUrl = matchingDoc.download_url || matchingDoc.url || matchingDoc.presigned_url;
                                                                                                                        const filename = matchingDoc.original_filename || matchingDoc.filename || image.name || 'observation_document';
                                                                                                                        if (downloadUrl) {
                                                                                                                            const link = document.createElement('a');
                                                                                                                            link.href = downloadUrl;
                                                                                                                            link.download = filename;
                                                                                                                            link.click();
                                                                                                                            return;
                                                                                                                        }
                                                                                                                    }
                                                                                                                }

                                                                                                                // Fallback to direct download URL
                                                                                                                if (image.download_url) {
                                                                                                                    const link = document.createElement('a');
                                                                                                                    link.href = image.download_url;
                                                                                                                    link.download = image.name || image.original_filename || 'observation_document';
                                                                                                                    link.click();
                                                                                                                } else if (image.s3_key) {
                                                                                                                    toast.info('Download URL not available. Please refresh the page.');
                                                                                                                } else {
                                                                                                                    toast.info('Download URL not available');
                                                                                                                }
                                                                                                            }}
                                                                                                            clickable
                                                                                                            color="success"
                                                                                                            variant="outlined"
                                                                                                            sx={{ fontSize: '0.7rem', height: 22 }}
                                                                                                        />
                                                                                                    </Box>
                                                                                                ))}
                                                                                            </Box>
                                                                                        ) : null}

                                                                                        {/* Legacy support for image_name field */}
                                                                                        {(!obs.images || obs.images.length === 0) && (!obs.images_data || obs.images_data.length === 0) && obs.image_name && (
                                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                                                                <Typography variant="caption" sx={{ color: 'info.main', fontStyle: 'italic' }}>
                                                                                                    📷 {obs.image_name}
                                                                                                </Typography>
                                                                                                <Chip
                                                                                                    icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                                                                    label="View"
                                                                                                    size="small"
                                                                                                    onClick={() => {
                                                                                                        // Check if observation has images_data with URLs
                                                                                                        if (obs.images_data && obs.images_data.length > 0) {
                                                                                                            // Use the first image for now
                                                                                                            const firstImage = obs.images_data[0];
                                                                                                            if (firstImage.url) {
                                                                                                                window.open(firstImage.url, '_blank');
                                                                                                            } else if (firstImage.s3_key) {
                                                                                                                toast.info('Image URL not available. Please refresh the page.');
                                                                                                            } else {
                                                                                                                toast.info('Image URL not available');
                                                                                                            }
                                                                                                        } else if (obs.images && obs.images.length > 0) {
                                                                                                            // Check the raw images field
                                                                                                            const firstImage = obs.images[0];
                                                                                                            if (firstImage.url) {
                                                                                                                window.open(firstImage.url, '_blank');
                                                                                                            } else if (firstImage.s3_key) {
                                                                                                                toast.info('Image URL not available. Please refresh the page.');
                                                                                                            } else {
                                                                                                                toast.info('Image URL not available');
                                                                                                            }
                                                                                                        } else {
                                                                                                            // Fallback to old image handling
                                                                                                            const docUrl = obs.document_url || obs.presigned_url || obs.image_url || obs.url ||
                                                                                                                obs.document?.presigned_url || obs.document?.url ||
                                                                                                                obs.attachment?.presigned_url || obs.attachment?.url ||
                                                                                                                obs.image_url;

                                                                                                            if (docUrl) {
                                                                                                                window.open(docUrl, '_blank');
                                                                                                            } else {
                                                                                                                toast.info(`No document URL found for: ${obs.image_name}`);
                                                                                                            }
                                                                                                        }
                                                                                                    }}
                                                                                                    clickable
                                                                                                    color="primary"
                                                                                                    variant="outlined"
                                                                                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                                                                                />
                                                                                                <Chip
                                                                                                    icon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                                                                    label="Download"
                                                                                                    size="small"
                                                                                                    onClick={() => {
                                                                                                        // Check if observation has images_data with URLs
                                                                                                        if (obs.images_data && obs.images_data.length > 0) {
                                                                                                            // Use the first image for now
                                                                                                            const firstImage = obs.images_data[0];
                                                                                                            if (firstImage.download_url) {
                                                                                                                const link = document.createElement('a');
                                                                                                                link.href = firstImage.download_url;
                                                                                                                link.download = firstImage.name || 'observation_document';
                                                                                                                link.click();
                                                                                                            } else if (firstImage.s3_key) {
                                                                                                                toast.info('Download URL not available. Please refresh the page.');
                                                                                                            } else {
                                                                                                                toast.info('Download URL not available');
                                                                                                            }
                                                                                                        } else if (obs.images && obs.images.length > 0) {
                                                                                                            // Check the raw images field
                                                                                                            const firstImage = obs.images[0];
                                                                                                            if (firstImage.download_url) {
                                                                                                                const link = document.createElement('a');
                                                                                                                link.href = firstImage.download_url;
                                                                                                                link.download = firstImage.name || 'observation_document';
                                                                                                                link.click();
                                                                                                            } else if (firstImage.s3_key) {
                                                                                                                toast.info('Download URL not available. Please refresh the page.');
                                                                                                            } else {
                                                                                                                toast.info('Download URL not available');
                                                                                                            }
                                                                                                        } else {
                                                                                                            // Fallback to old image handling
                                                                                                            const docUrl = obs.document_url || obs.presigned_url || obs.image_url || obs.url ||
                                                                                                                obs.document?.presigned_url || obs.document?.url ||
                                                                                                                obs.attachment?.presigned_url || obs.attachment?.url ||
                                                                                                                obs.image_url;

                                                                                                            if (docUrl) {
                                                                                                                const link = document.createElement('a');
                                                                                                                link.href = docUrl;
                                                                                                                link.download = obs.image_name || 'observation_document';
                                                                                                                link.click();
                                                                                                            } else {
                                                                                                                toast.info(`No download URL found for: ${obs.image_name}`);
                                                                                                            }
                                                                                                        }
                                                                                                    }}
                                                                                                    clickable
                                                                                                    color="success"
                                                                                                    variant="outlined"
                                                                                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                                                                                />
                                                                                            </Box>
                                                                                        )}
                                                                                    </Box>
                                                                                );
                                                                            })}
                                                                        </Box>
                                                                    )}

                                                                    {/* Remove the separate "Attached Documents" section - documents are now shown under observations */}

                                                                    {/* Notes Section - If available */}
                                                                    {report.notes && report.notes.length > 0 && (
                                                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                                                📝 Notes ({report.notes.length})
                                                                            </Typography>
                                                                            {report.notes.map((note, idx) => (
                                                                                <Box key={idx} sx={{ mb: 0.5, pl: 1 }}>
                                                                                    <Typography variant="caption" display="block">
                                                                                        • {note.remark}
                                                                                    </Typography>
                                                                                </Box>
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            }
                                                        />
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            {/* Show View button - behavior depends on ui_visible permission */}
                                                            {user?.role?.permissions?.reporting?.view === true ? (
                                                                // User has full reporting access - navigate to report page
                                                                <Chip
                                                                    icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                                                    label="View"
                                                                    size="small"
                                                                    onClick={() => navigate(`/reports/details/${report.id}`)}
                                                                    clickable
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                />
                                                            ) : (
                                                                // User without UI access - show PDF directly if available
                                                                <Chip
                                                                    icon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                                                    label={report.has_pdf ? "View PDF" : "View"}
                                                                    size="small"
                                                                    onClick={() => {
                                                                        if (report.has_pdf && report.pdf_url) {
                                                                            window.open(report.pdf_url, '_blank');
                                                                        } else {
                                                                            toast.info('PDF not available yet for this report');
                                                                        }
                                                                    }}
                                                                    clickable
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                />
                                                            )}
                                                            {/* Download PDF button when PDF is available */}
                                                            {report.has_pdf && report.pdf_download_url && (
                                                                <Chip
                                                                    icon={<DownloadIcon sx={{ fontSize: 16 }} />}
                                                                    label="Download"
                                                                    size="small"
                                                                    onClick={async () => {
                                                                        try {
                                                                            const reportType = report.report_type ? 'details-reports' : 'flash-reports';
                                                                            console.log(`📡 Calling ${reportType} download API for report ${report.id}`);
                                                                            const response = await api.get(`/api/service-orders/${reportType}/${report.id}/download/`);
                                                                            console.log('✅ Download response:', response.data);
                                                                            const downloadUrl = response.data?.download_url;
                                                                            if (downloadUrl) {
                                                                                console.log('🔗 Opening download URL');
                                                                                window.open(downloadUrl, '_blank');
                                                                            } else {
                                                                                toast.error('Download URL not available');
                                                                            }
                                                                        } catch (error) {
                                                                            console.error('❌ Download API error:', error);
                                                                            // Check for quota exceeded (429)
                                                                            if (error.response?.status === 429) {
                                                                                const message = error.response?.data?.error || 'Storage quota exceeded. Please contact support.';
                                                                                toast.error(message);
                                                                            } else {
                                                                                toast.error('Download failed: ' + (error.response?.data?.error || error.message));
                                                                            }
                                                                        }
                                                                    }}
                                                                    clickable
                                                                    color="success"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                />
                                                            )}
                                                            {/* Email button for completed detailed reports */}
                                                            {report.report_type && report.has_pdf && (
                                                                <Chip
                                                                    icon={<EmailIcon sx={{ fontSize: 16 }} />}
                                                                    label="Email"
                                                                    size="small"
                                                                    onClick={() => setEmailReportTarget({ id: report.id, title: report.report_no || report.report_type })}
                                                                    clickable
                                                                    color="info"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                />
                                                            )}
                                                            {/* Edit button only for users with full UI access and change permission */}
                                                            {user?.role?.permissions?.reporting?.view === true && user?.role?.permissions?.reporting?.change === true && (
                                                                <Chip
                                                                    icon={<EditIcon sx={{ fontSize: 16 }} />}
                                                                    label="Edit"
                                                                    size="small"
                                                                    onClick={() => navigate(`/reports/details/${report.id}/edit`)}
                                                                    clickable
                                                                    color="warning"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}

                                    {/* No Detailed Reports Message */}
                                    {(!order.reports.details_reports || order.reports.details_reports.length === 0) && (
                                        <Box sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No detailed reports created yet
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Detailed reports will appear here once created for this service order
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No reports available for this service order.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>

                {/* Equipment Assignment Section - Visible to all users, but only editable to users with inventory permissions */}
                <Box sx={{ mb: 3 }}>


                    <Typography variant="h6" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        color: 'primary.main',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        pb: 1
                    }}>
                        Equipment Assignment
                    </Typography>
                    <AssignmentManager
                        title="Equipment Assignment"
                        type="equipment"
                        currentAssignments={order?.equipment || []}
                        availableItems={availableEquipment}
                        onAdd={async (equipmentData) => {
                            // Check if service order is closed
                            if (isClosed) {
                                toast.error('Cannot assign equipment. Service order is closed or cancelled.');
                                return false; // Signal that operation was blocked
                            }

                            try {
                                // Backend expects equipment_ids array, extract IDs from equipmentData
                                const equipmentIds = equipmentData.map(item => item.id);
                                await serviceOrderService.assignEquipment(id, equipmentIds);
                                // Refresh the service order data
                                await fetchOrder();
                                // Refresh available inventory
                                await fetchAvailableInventory();
                                return true; // Signal that operation succeeded
                            } catch (error) {
                                console.error('Error assigning equipment:', error);
                                toast.error(`Failed to assign equipment: ${error.response?.data?.error || error.message}`);
                                throw error; // Re-throw to trigger error handling in AssignmentManager
                            }
                        }}
                        onRemove={async (assignmentId) => {
                            // Check if service order is closed
                            if (isClosed) {
                                toast.error('Cannot remove equipment. Service order is closed or cancelled.');
                                return false; // Signal that operation was blocked
                            }

                            try {
                                // Use the proper service function
                                await serviceOrderService.removeEquipment(id, assignmentId);

                                // Refresh the service order data
                                await fetchOrder();

                                // IMPORTANT: Refresh available inventory to show newly freed equipment
                                // Equipment becomes 'available' again and can be assigned to other service orders
                                await fetchAvailableInventory();

                                return true; // Signal that operation succeeded
                            } catch (error) {
                                console.error('Error removing equipment:', error);
                                toast.error(`Failed to remove equipment: ${error.response?.data?.error || error.message}`);
                                throw error; // Re-throw to trigger error handling in AssignmentManager
                            }
                        }}
                        onUpdate={fetchOrder}
                        canEdit={canEditField('equipment') && !isClosed}
                        showAddButton={!isClosed}
                        maxHeight="400px"
                        serviceOrderId={id}
                    />
                </Box>

                {/* Consumables Assignment Section - Visible to all users, but only editable to users with inventory permissions */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{
                        mb: 2,
                        fontWeight: 'bold',
                        color: 'primary.main',
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        pb: 1
                    }}>
                        Consumables Assignment
                    </Typography>
                    <AssignmentManager
                        title="Consumables Assignment"
                        type="consumable"
                        currentAssignments={order?.consumables || []}
                        availableItems={availableConsumables}
                        onAdd={async (consumableData) => {
                            // Check if service order is closed
                            if (isClosed) {
                                toast.error('Cannot assign consumables. Service order is closed or cancelled.');
                                return false; // Signal that operation was blocked
                            }

                            try {
                                const lines = order?.consumables || [];
                                for (const item of consumableData) {
                                    const addQty = parseFloat(item.quantity) || 1;
                                    const existing = lines.find(
                                        (c) => c.consumable === item.id
                                            || c.consumable_data?.id === item.id
                                    );
                                    if (existing) {
                                        const currentQty = parseFloat(existing.quantity) || 0;
                                        await serviceOrderService.updateConsumable(
                                            id,
                                            item.id,
                                            currentQty + addQty
                                        );
                                    } else {
                                        await serviceOrderService.addConsumable(id, {
                                            consumable: item.id,
                                            quantity: addQty
                                        });
                                    }
                                }

                                // Refresh the service order data
                                await fetchOrder();
                                // Refresh available inventory
                                await fetchAvailableInventory();
                                return true; // Signal that operation succeeded
                            } catch (error) {
                                console.error('Error assigning consumables:', error);
                                toast.error(`Failed to assign consumables: ${error.response?.data?.error || error.message}`);
                                throw error; // Re-throw to trigger error handling in AssignmentManager
                            }
                        }}
                        onRemove={async (assignmentId) => {
                            // Check if service order is closed
                            if (isClosed) {
                                toast.error('Cannot remove consumable. Service order is closed or cancelled.');
                                return false; // Signal that operation was blocked
                            }

                            try {
                                // Use the proper service function
                                await serviceOrderService.removeConsumable(id, assignmentId);

                                // Refresh the service order data
                                await fetchOrder();

                                // Refresh available inventory to show updated quantities
                                // Note: Consumables are consumed and need inventory replenishment
                                await fetchAvailableInventory();

                                return true; // Signal that operation succeeded
                            } catch (error) {
                                console.error('Error removing consumable:', error);
                                toast.error(`Failed to remove consumable: ${error.response?.data?.error || error.message}`);
                                throw error; // Re-throw to trigger error handling in AssignmentManager
                            }
                        }}
                        onUpdate={fetchOrder}
                        canEdit={canEditField('consumables') && !isClosed}
                        showAddButton={!isClosed}
                        maxHeight="400px"
                        serviceOrderId={id}
                    />
                </Box>

            </Box>{/* end full-width content */}

            {/* ── Activity Log — sliding drawer from right ─────────────────────── */}
            <Drawer
                anchor="right"
                open={showActivityLog}
                onClose={() => setShowActivityLog(false)}
                variant="temporary"
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 420 },
                        maxWidth: '100vw',
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                    {/* Close button row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 1, pt: 1 }}>
                        <Tooltip title="Close Activity Log">
                            <IconButton size="small" onClick={() => setShowActivityLog(false)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {/* Activity Log component fills remaining space */}
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <ServiceOrderActivityLog ref={activityLogRef} serviceOrderId={id} isClosed={isClosed} />
                    </Box>
                </Box>
            </Drawer>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Service Order</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this service order? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton onClick={() => setDeleteDialogOpen(false)}>Cancel</SecondaryButton>
                    <FilledDestructiveButton onClick={handleDelete}>
                        Delete
                    </FilledDestructiveButton>
                </DialogActions>
            </Dialog>

            {/* Document Upload Dialog */}
            <DocumentUploadDialog
                open={uploadDialogOpen}
                onClose={handleCloseUploadDialog}
                referenceType="service_order"
                referenceId={order.id}
                title="Upload Invoice or Document"
                onSuccess={handleUploadSuccess}
            />

            {/* Payment Dialog - Intercepted from "Record Payment" transition */}
            <PaymentDialog
                open={paymentDialogOpen}
                invoiceId={paymentDialogInvoiceId}
                balanceDue={order?.invoice_balance_due}
                onClose={() => {
                    setPaymentDialogOpen(false);
                    setPendingPaymentTransition(null);
                }}
                onSuccess={async (remainingBalance) => {
                    setPaymentDialogOpen(false);
                    setInvoicePanelKey(k => k + 1);
                    // Refresh order and transitions to reflect latest state from backend
                    const updatedOrder = await fetchOrder();
                    await fetchTransitions();
                    await refreshNotifications();

                    // If balance becomes 0 and SO is not yet in 'Paid' state, trigger transition as fallback
                    if (remainingBalance === 0 && pendingPaymentTransition) {
                        const { transition, toStateId } = pendingPaymentTransition;
                        const currentStateName = (updatedOrder?.current_state?.name || order?.current_state?.name || '').toLowerCase();
                        if (currentStateName !== 'paid') {
                            await performTransition(transition, toStateId);
                        }
                    }
                    setPendingPaymentTransition(null);
                }}
            />

            {/* Job Selection Dialog - Select sub-service orders to invoice */}
            <JobSelectionDialog
                open={jobSelectionDialogOpen}
                parentSoId={order?.id}
                onClose={() => setJobSelectionDialogOpen(false)}
                onSuccess={(invoice) => {
                    setJobSelectionDialogOpen(false);
                    // Redirect to invoice edit form
                    navigate(`/invoicing/${invoice.id}/edit`);
                }}
            />

            {/* Invoice View Preferences Dialog — for SOs without sub-orders */}
            <InvoiceViewPreferencesDialog
                open={viewPrefsDialogOpen}
                onClose={() => setViewPrefsDialogOpen(false)}
                companyTier={order?.company_tier || order?.company_details?.tier || order?.company?.tier || null}
                onConfirm={async (viewType, tierId, componentIds) => {
                    try {
                        const response = await invoicingService.createFromServiceOrder(
                            order.id, viewType, tierId, componentIds
                        );
                        toast.success('Invoice created successfully');
                        navigate(`/invoicing/${response.data.id}/edit`);
                    } catch (err) {
                        toast.error(err.response?.data?.error || 'Failed to create invoice');
                    }
                }}
            />

            {/* Quotation Upload Dialog - shown when transitioning to Quoted state */}
            <QuotationUploadDialog
                open={quotationDialogOpen}
                onClose={() => {
                    setQuotationDialogOpen(false);
                    setPendingQuotationTransition(null);
                    setSelectedTransition(null);
                }}
                serviceOrderId={order.id}
                serviceOrderDisplay={order?.display_name || order?.so_number || `SO-${order.id}`}
                companyId={order?.company?.id ?? order?.company_id}
                primaryContactUserDetails={order?.company?.primary_contact_user_details}
                onProceed={handleQuotationProceed}
            />

            {/* Email Detailed Report Dialog */}
            <SendReportEmailDialog
                open={Boolean(emailReportTarget)}
                onClose={() => setEmailReportTarget(null)}
                reportId={emailReportTarget?.id}
                reportTitle={emailReportTarget?.title || 'Detailed Report'}
            />
        </Box>
    );
};

export default ServiceOrderDetail; 